import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAllUsers } from '../../hooks/useAllUsers';
import { useNavigate, useParams } from 'react-router-dom';
import {
  FiArrowLeft, FiSave, FiUser, FiMail, FiPhone,
  FiLock, FiEye, FiEyeOff, FiShield, FiInfo, FiAlertCircle, FiCheckCircle,
  FiFileText, FiUpload, FiTrash2, FiFile, FiDownload,
} from 'react-icons/fi';
import { api } from '@crm/shared';
import Layout from '../../components/Layout';
import authService from '../../services/authService';
import { toast } from 'react-toastify';
import DateInput from '../../components/DateInput';
import { fmtDate } from '../../utils/dateUtils';
import { isAdminLike, isTrueAdmin } from '../../utils/roleAccess';

/* ─── Design tokens ─── */
const C = {
  navy:'#0D3B5E', navyD:'#082B47', navyL:'#EBF4FF', navyM:'#1A5276',
  gold:'#F5A623', goldL:'#FEF3DC',
  green:'#16A34A', greenL:'#DCFCE7',
  red:'#DC2626', redL:'#FEF2F2', redB:'#FECACA',
  sky:'#0284C7', skyL:'#DBEAFE',
  purple:'#7C3AED', purpleL:'#EDE9FE',
  border:'#E5E7EB', borderL:'#F3F4F6',
  white:'#FFFFFF', t1:'#111827', t2:'#374151', t3:'#6B7280', t4:'#9CA3AF',
};

const DESIGNATION_OPTIONS = ['Sales Executive','Team Lead','Sales Manager','Senior Manager','Branch Manager','Director','Accountant','FOS'];
const GENDER_OPTIONS = [{ value:'male',label:'Male' },{ value:'female',label:'Female' },{ value:'other',label:'Other' }];

const DOC_TYPES = [
  { value:'ID_PROOF',       label:'ID Proof'           },
  { value:'ADDRESS_PROOF',  label:'Address Proof'      },
  { value:'JOINING_LETTER', label:'Joining Letter'     },
  { value:'AGREEMENT',      label:'Agreement'          },
  { value:'QUALIFICATION',  label:'Qualification Cert' },
  { value:'OTHER',          label:'Other'              },
];
const docTypeLabel = (v) => DOC_TYPES.find(d => d.value === v)?.label || v;

const getPermissions = (user) => {
  if (!user || !user.role || typeof user.role !== 'object') return [];
  return Array.isArray(user.role.permissions) ? user.role.permissions : [];
};

const hasPermission = (user, code) => {
  if (!user) return false;
  if (user?.role === null || user?.role === undefined) return true;
  return getPermissions(user).some((permission) => permission?.code === code);
};

const DOC_INP_ST = {
  width:'100%', height:36, border:'1.5px solid #E5E7EB', borderRadius:8,
  padding:'0 11px', fontSize:13, color:'#111827', background:'#FAFBFC',
  outline:'none', fontFamily:"'Plus Jakarta Sans',sans-serif",
  boxSizing:'border-box', transition:'border 0.13s', appearance:'none', WebkitAppearance:'none',
};
const docFocus = (e) => { e.target.style.borderColor='#0D3B5E'; e.target.style.background='#fff'; e.target.style.boxShadow='0 0 0 3px rgba(13,59,94,0.08)'; };
const docBlur  = (e) => { e.target.style.borderColor='#E5E7EB'; e.target.style.background='#FAFBFC'; e.target.style.boxShadow='none'; };

const fileName = (url) => {
  if (!url) return 'Document';
  try { return decodeURIComponent(url.split('/').pop().split('?')[0]) || 'Document'; }
  catch { return 'Document'; }
};

// EMPTY_FORM uses cutoff_amount (matching backend field name)
const EMPTY_FORM = {
  first_name:'', last_name:'', email:'', password:'', confirm_password:'',
  mobile_number:'', dob:'', gender:'', role:'', reporting_manager:'',
  designation:'', address:'', salary:'', cutoff_amount:'', incentive_percentage:'',
};

const inputSt = (hasErr=false) => ({
  display:'block', width:'100%', height:40, padding:'0 12px',
  border:`1.5px solid ${hasErr?C.red:C.border}`, borderRadius:9,
  fontSize:13, fontFamily:'inherit', color:C.t1, background: hasErr?'#FFF9F9':'#FAFBFC',
  outline:'none', boxSizing:'border-box', transition:'all 0.15s',
});
const onFocus = (e) => { e.target.style.borderColor=C.navy; e.target.style.background='#fff'; e.target.style.boxShadow='0 0 0 3px rgba(13,59,94,0.08)'; };
const onBlur  = (e, hasErr=false) => { e.target.style.borderColor=hasErr?C.red:C.border; e.target.style.background=hasErr?'#FFF9F9':'#FAFBFC'; e.target.style.boxShadow='none'; };

const Field = ({ label, required, error, hint, children, span2=false }) => (
  <div className="user-field" style={{ gridColumn: span2 ? 'span 2' : 'auto', display:'flex', flexDirection:'column', gap:5 }}>
    <label style={{ fontSize:12, fontWeight:700, color: error ? C.red : C.t2, letterSpacing:'0.01em' }}>
      {label}{required && <span style={{ color:C.red, marginLeft:2 }}>*</span>}
    </label>
    {children}
    {hint && !error && <div style={{ fontSize:11.5, color:C.t4, lineHeight:1.5 }}>{hint}</div>}
    {error && (
      <div style={{ display:'flex', alignItems:'center', gap:4, fontSize:11.5, color:C.red, fontWeight:600 }}>
        <FiAlertCircle size={11}/> {error}
      </div>
    )}
  </div>
);

const Section = ({ icon, title, desc, accentColor=C.navy, children }) => (
  <div className="user-section" style={{ background:C.white, borderRadius:12, border:`1px solid ${C.border}`, overflow:'hidden', boxShadow:'0 2px 6px rgba(13,59,94,0.06)' }}>
    <div style={{ padding:'14px 20px', borderBottom:`1px solid ${C.borderL}`, background:'#FAFBFC', borderLeft:`3px solid ${accentColor}`, display:'flex', alignItems:'center', gap:12 }}>
      <div style={{ width:34, height:34, borderRadius:9, background:accentColor+'18', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
        <span style={{ color:accentColor, display:'flex' }}>{icon}</span>
      </div>
      <div>
        <div style={{ fontSize:14, fontWeight:800, color:C.t1, letterSpacing:'-0.02em' }}>{title}</div>
        {desc && <div style={{ fontSize:12, color:C.t4, marginTop:1 }}>{desc}</div>}
      </div>
    </div>
    <div className="uf-sec-grid" style={{ padding:'18px 20px' }}>
      {children}
    </div>
  </div>
);

const Sh = ({ h=20, w='100%', r=6 }) => (
  <div style={{ height:h, width:w, borderRadius:r, background:'linear-gradient(90deg,#F1F5F9 25%,#E2E8F0 50%,#F1F5F9 75%)', backgroundSize:'400px 100%', animation:'cfShimmer 1.4s infinite' }}/>
);

export default function UserForm() {
  const navigate = useNavigate();
  const { id }   = useParams();
  const isEdit   = Boolean(id);

  const { users: allActiveUsers } = useAllUsers();
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [roles,    setRoles]    = useState([]);
  const managers = allActiveUsers;
  const [currentUser, setCurrentUser] = useState(null);
  const [loading,  setLoading]  = useState(false);
  const [saving,   setSaving]   = useState(false);
  const [errors,   setErrors]   = useState({});
  const [showPwd,  setShowPwd]  = useState(false);
  const [showCfm,  setShowCfm]  = useState(false);
  const [changePasswordMode, setChangePasswordMode] = useState(!isEdit);
  const [profilePhoto, setProfilePhoto] = useState(null);
  const [profilePhotoPreview, setProfilePhotoPreview] = useState(null);
  const profileRef = useRef(null);

  useEffect(() => {
    if (profilePhoto) {
      const url = URL.createObjectURL(profilePhoto);
      setProfilePhotoPreview(url);
      return () => URL.revokeObjectURL(url);
    } else {
      setProfilePhotoPreview(null);
    }
  }, [profilePhoto]);

  const [documents,    setDocuments]    = useState([]);
  const [docType,      setDocType]      = useState('ID_PROOF');
  const [docFile,      setDocFile]      = useState(null);
  const [docUploading, setDocUploading] = useState(false);
  const [docDeleting,  setDocDeleting]  = useState(null);
  const [docsLoading,  setDocsLoading]  = useState(false);
  const fileRef = useRef(null);
  const adminLikeUser = isAdminLike(currentUser);
  const hasFullUserAccess = !!currentUser && (
    isTrueAdmin(currentUser) ||
    (adminLikeUser && hasPermission(currentUser, 'user_read')) ||
    hasPermission(currentUser, 'user_create') ||
    hasPermission(currentUser, 'user_delete')
  );
  const canCreateUsers = hasPermission(currentUser, 'user_create');
  const canUpdateUsers = hasPermission(currentUser, 'user_update');
  const isSelfProfileMode = !!currentUser?.id && isEdit && String(currentUser.id) === String(id) && canUpdateUsers && !hasFullUserAccess;

  const extractRoles = (d) => {
    if (d?.status==='success' && Array.isArray(d?.data)) return d.data;
    if (Array.isArray(d?.data))    return d.data;
    if (Array.isArray(d?.results)) return d.results;
    if (Array.isArray(d))          return d;
    return [];
  };
  const extractUsers = (d) => {
    if (d?.status==='success' && Array.isArray(d?.results)) return d.results;
    if (Array.isArray(d?.results)) return d.results;
    if (Array.isArray(d))          return d;
    if (Array.isArray(d?.data))    return d.data;
    return [];
  };
  const extractUser = (response) => {
    const data = response?.data;
    if (data?.data && typeof data.data === 'object' && !Array.isArray(data.data)) return data.data;
    return data && typeof data === 'object' && !Array.isArray(data) ? data : null;
  };

  const init = useCallback(async () => {
    setLoading(true);
    try {
      const storedUser = (() => {
        try { return JSON.parse(localStorage.getItem('user') || 'null'); }
        catch { return null; }
      })();
      setCurrentUser(storedUser);
      if (!isEdit && !(
        isTrueAdmin(storedUser) ||
        (isAdminLike(storedUser) && hasPermission(storedUser, 'user_read')) ||
        hasPermission(storedUser, 'user_create') ||
        hasPermission(storedUser, 'user_delete')
      )) {
        toast.error('Only admin-level roles can create users');
        navigate('/users', { replace: true });
        return;
      }
      setChangePasswordMode(!isEdit);
      setShowPwd(false);
      setShowCfm(false);
      setErrors((current) => ({
        ...current,
        password: '',
        confirm_password: '',
      }));
      const promises = [api.getRoles().catch(() => null)];
      if (isEdit) promises.push(api.getUserById(id));
      const [rolesRes, userRes] = await Promise.all(promises);
      setRoles(rolesRes ? extractRoles(rolesRes?.data) : []);
      if (isEdit && userRes) {
        const u = extractUser(userRes);
        if (!u) throw new Error('User payload missing');
        setFormData({
          first_name:   u.first_name   || '',
          last_name:    u.last_name    || '',
          email:        u.email        || '',
          password:     '',
          confirm_password: '',
          mobile_number:   u.mobile_number   || '',
          dob:             u.dob             || '',
          gender:          u.gender          || '',
          // role comes as object {id, name, ...} — extract id
          role:            String(u.role?.id ?? u.role ?? ''),
          // reporting_manager comes as object {id, first_name, ...} — extract id
          reporting_manager: String(u.reporting_manager?.id ?? u.reporting_manager ?? ''),
          designation:       u.designation   || '',
          address:           u.address       || '',
          salary:            String(u.salary              ?? ''),
          // Backend field is cutoff_amount (not cut_off_amount)
          cutoff_amount:     String(u.cutoff_amount       ?? ''),
          incentive_percentage: String(u.incentive_percentage ?? ''),
        });
        if (u.profile_picture) setProfilePhotoPreview(u.profile_picture);
        try {
          setDocsLoading(true);
          const docsRes = await api.getUserDocuments(id);
          const docsData = docsRes?.data;
          const docsArr = Array.isArray(docsData?.results) ? docsData.results
            : Array.isArray(docsData) ? docsData : [];
          setDocuments(docsArr);
        } catch { /* non-critical */ }
        finally { setDocsLoading(false); }
      }
    } catch (err) {
      console.error('Form init error:', err);
      toast.error('Failed to load form data.');
    } finally { setLoading(false); }
  }, [id, isEdit]);

  useEffect(() => { init(); }, [init]);

  const set = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]:'' }));
  };
  const handleChange = (e) => set(e.target.name, e.target.value);

  const toggleChangePasswordMode = () => {
    setChangePasswordMode((prev) => {
      const next = !prev;
      if (!next) {
        setFormData((current) => ({
          ...current,
          password: '',
          confirm_password: '',
        }));
        setErrors((current) => ({
          ...current,
          password: '',
          confirm_password: '',
        }));
        setShowPwd(false);
        setShowCfm(false);
      }
      return next;
    });
  };

  const validate = () => {
    const errs = {};
    if (!formData.first_name.trim())  errs.first_name  = 'First name is required';
    if (!formData.last_name.trim())   errs.last_name   = 'Last name is required';
    if (!formData.email.trim())       errs.email       = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) errs.email = 'Enter a valid email';
    const mob = formData.mobile_number.trim();
    if (!mob)                         errs.mobile_number = 'Mobile number is required';
    else if (!/^\d{10}$/.test(mob))   errs.mobile_number = 'Enter a valid 10-digit number';
    if (!isSelfProfileMode && !formData.role) errs.role = 'Please select a role';
    
    if (!isSelfProfileMode) {
      const cutoff = formData.cutoff_amount?.trim();
      if (!cutoff) errs.cutoff_amount = 'Cut-off amount is required';
      else if (isNaN(cutoff) || parseFloat(cutoff) < 0) errs.cutoff_amount = 'Enter a valid amount';
      
      const incentive = formData.incentive_percentage?.trim();
      if (!incentive) errs.incentive_percentage = 'Incentive % is required';
      else if (isNaN(incentive) || parseFloat(incentive) < 0) errs.incentive_percentage = 'Enter a valid percentage';
      else if (parseFloat(incentive) > 100) errs.incentive_percentage = 'Cannot exceed 100%';
    }
    
    if (!isEdit) {
      if (!formData.password)                              errs.password         = 'Password is required';
      else if (formData.password.length < 8)               errs.password         = 'Minimum 8 characters';
      if (formData.password !== formData.confirm_password) errs.confirm_password = 'Passwords do not match';
    } else if (changePasswordMode) {
      if (!formData.password)                              errs.password         = 'Password is required';
      if (formData.password.length < 8)                    errs.password         = 'Minimum 8 characters';
      if (formData.password !== formData.confirm_password) errs.confirm_password = 'Passwords do not match';
    }
    return errs;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) {
      setErrors(errs);
      document.querySelector(`[name="${Object.keys(errs)[0]}"]`)?.scrollIntoView({ behavior:'smooth', block:'center' });
      return;
    }
    setSaving(true);
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');

      // Always use FormData to handle file uploads properly
      const fd = new FormData();
      fd.append('username',        formData.email.trim());
      fd.append('first_name',      formData.first_name.trim());
      fd.append('last_name',       formData.last_name.trim());
      fd.append('email',           formData.email.trim());
      fd.append('mobile_number',   formData.mobile_number.trim());
      fd.append('dob',             formData.dob || '');
      fd.append('gender',          formData.gender || '');
      fd.append('address',         formData.address.trim() || '');
      if (!isSelfProfileMode) {
        fd.append('role_id', formData.role ? parseInt(formData.role) : '');
        fd.append('reporting_manager_id', formData.reporting_manager ? parseInt(formData.reporting_manager) : '');
        fd.append('designation', formData.designation || '');
        fd.append('is_active', 'true');
      }
      
      if (!isSelfProfileMode) {
        const salary = formData.salary?.trim();
        if (salary && salary !== '') {
          fd.append('salary', salary);
        }
        
        const cutoff = formData.cutoff_amount?.trim();
        if (cutoff && cutoff !== '') {
          fd.append('cutoff_amount', cutoff);
        }
        
        const incentive = formData.incentive_percentage?.trim();
        if (incentive && incentive !== '') {
          fd.append('incentive_percentage', incentive);
        }
      }
      
      // Handle profile picture
      if (profilePhoto) {
        fd.append('profile_picture', profilePhoto);
      }
      
      // Handle password
      if ((!isEdit || changePasswordMode) && formData.password) {
        fd.append('password', formData.password);
        fd.append('confirm_password', formData.confirm_password);
      }
      
      if (user?.id) { 
        fd.append('created_by', user.id); 
        fd.append('updated_by', user.id); 
      }
      
      if (isEdit) { 
        await api.patchUser(id, fd);
        if (String(user?.id) === String(id)) {
          const refreshed = await api.getUserById(id);
          const refreshedUser = extractUser(refreshed);
          if (refreshedUser) authService.updateUser(refreshedUser);
        }
      } else { 
        await api.createUser(fd); 
      }
      
      toast.success(`User ${isEdit ? 'updated' : 'created'} successfully`);
      navigate('/users');
    } catch (err) {
      console.error('Save user error:', err);
      if (err?.response?.data && typeof err.response.data === 'object') {
        const apiErrs = {};
        const rawErrors = err.response.data;
        
        // Handle nested error objects
        Object.entries(rawErrors).forEach(([f, msgs]) => {
          if (typeof msgs === 'string') {
            apiErrs[f] = msgs;
          } else if (Array.isArray(msgs)) {
            apiErrs[f] = msgs[0];
          } else if (typeof msgs === 'object' && msgs !== null) {
            // Handle nested validation errors like {profile_picture: [...]}
            Object.entries(msgs).forEach(([nestedField, nestedMsgs]) => {
              const key = nestedField === '0' ? f : `${f}_${nestedField}`;
              apiErrs[key] = Array.isArray(nestedMsgs) ? nestedMsgs[0] : String(nestedMsgs);
            });
          }
        });
        
        setErrors(apiErrs);
        toast.error('Please fix the highlighted errors');
      } else {
        toast.error(err?.message || err?.detail || `Failed to ${isEdit ? 'update' : 'create'} user`);
      }
    } finally { setSaving(false); }
  };

  const handleDocUpload = async () => {
    if (!docFile) { toast.error('Please select a file'); return; }
    if (!isEdit || !id) { toast.error('Save the user first, then upload documents'); return; }
    setDocUploading(true);
    try {
      const currentUser = (() => { try { return JSON.parse(localStorage.getItem('user') || '{}'); } catch { return {}; } })();
      const fd = new FormData();
      fd.append('user_id',      id);
      fd.append('document_type', docType);
      fd.append('file',          docFile);
      const res = await api.createUserDocument(fd);
      setDocuments(prev => [...prev, res.data]);
      setDocFile(null);
      if (fileRef.current) fileRef.current.value = '';
      toast.success('Document uploaded');
    } catch (err) {
      toast.error(err?.message || 'Upload failed');
    } finally { setDocUploading(false); }
  };

  const handleDocDelete = async (docId) => {
    if (!window.confirm('Delete this document? This cannot be undone.')) return;
    setDocDeleting(docId);
    try {
      await api.deleteUserDocument(docId);
      setDocuments(prev => prev.filter(d => d.id !== docId));
      toast.success('Document deleted');
    } catch (err) {
      toast.error(err?.message || 'Failed to delete document');
    } finally { setDocDeleting(null); }
  };

  const errCount = Object.values(errors).filter(Boolean).length;

  if (loading) {
    return (
      <Layout pageTitle={isEdit ? 'Edit User' : 'Add User'}>
        <style>{`@keyframes cfShimmer{0%{background-position:-400px 0}100%{background-position:400px 0}} @keyframes cfSpin{from{transform:rotate(0)}to{transform:rotate(360deg)}}`}</style>
        <div style={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', padding:'72px 0', gap:14 }}>
          <div style={{ width:40, height:40, borderRadius:'50%', border:`3px solid ${C.navyL}`, borderTopColor:C.navy, animation:'cfSpin 0.7s linear infinite' }}/>
          <span style={{ fontSize:13.5, color:C.t4 }}>Loading form…</span>
        </div>
      </Layout>
    );
  }

  return (
    <>
      <style>{`
        @keyframes cfShimmer { 0%{background-position:-400px 0} 100%{background-position:400px 0} }
        @keyframes cfSpin    { from{transform:rotate(0)} to{transform:rotate(360deg)} }
        @keyframes cfFadeUp  { from{opacity:0;transform:translateY(6px)} to{opacity:1;transform:translateY(0)} }
        @media (max-width: 768px) {
          .user-form-container { grid-template-columns: 1fr !important; }
          .user-section-grid { grid-template-columns: 1fr !important; }
          .user-field[style*="span 2"] { grid-column: 1 !important; }
          .user-action-bar { flex-direction: column !important; align-items: stretch !important; }
          .user-hero-banner { padding: 16px 20px !important; }
        }
        .uf-grid{display:grid;grid-template-columns:1fr 1fr;gap:14px;align-items:start;}
        .uf-sec-grid{display:grid;grid-template-columns:1fr 1fr;gap:16px 20px;}
        @media(max-width:900px){
          .uf-grid{grid-template-columns:1fr!important;}
          .uf-sec-grid{grid-template-columns:1fr!important;}
        }
      `}</style>
      <Layout pageTitle={isEdit ? 'Edit User' : 'Add User'}>
        <div style={{ display:'flex', flexDirection:'column', gap:14, fontFamily:"'Plus Jakarta Sans',sans-serif", animation:'cfFadeUp 0.22s ease both' }}>

          {/* HERO */}
          <div className="user-hero-banner" style={{ borderRadius:14, padding:'20px 28px', display:'flex', alignItems:'center', gap:14, background:'linear-gradient(120deg,#0D3B5E,#1A5276)', boxShadow:'0 4px 20px rgba(13,59,94,0.20)', position:'relative', overflow:'hidden', flexWrap:'wrap' }}>
            <div style={{ position:'absolute', top:0, left:0, right:0, height:3, background:'linear-gradient(90deg,#F5A623,#FBBF4A,transparent)' }}/>
            <button onClick={() => navigate('/users')}
              style={{ width:38, height:38, borderRadius:10, background:'rgba(255,255,255,0.11)', border:'1px solid rgba(255,255,255,0.22)', color:'#fff', display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', flexShrink:0, transition:'all 0.12s' }}
              onMouseEnter={e => e.currentTarget.style.background='rgba(255,255,255,0.20)'}
              onMouseLeave={e => e.currentTarget.style.background='rgba(255,255,255,0.11)'}>
              <FiArrowLeft size={17}/>
            </button>
            <div>
              <div style={{ fontSize:11, fontWeight:600, color:'rgba(255,255,255,0.55)', marginBottom:4, letterSpacing:'0.06em', textTransform:'uppercase' }}>User Management</div>
              <h2 style={{ margin:0, color:'#fff', fontSize:20, fontWeight:800, letterSpacing:'-0.03em', lineHeight:1.2 }}>{isEdit ? 'Edit User' : 'Add New User'}</h2>
              <div style={{ fontSize:12.5, color:'rgba(255,255,255,0.55)', marginTop:4 }}>
                {isEdit ? 'Update user information and role assignment' : 'Create a new system user and assign a role'}
              </div>
            </div>
          </div>

          {/* ERROR SUMMARY */}
          {errCount > 0 && (
            <div style={{ background:C.redL, border:`1.5px solid ${C.redB}`, borderRadius:10, padding:'12px 16px', display:'flex', alignItems:'center', gap:8, fontSize:13, color:C.red, fontWeight:600 }}>
              <FiAlertCircle size={15}/> {errCount} field{errCount !== 1 ? 's' : ''} need{errCount === 1 ? 's' : ''} attention
            </div>
          )}

          {/* FORM */}
          <form onSubmit={handleSubmit} noValidate>
            <div className="uf-grid">

              {/* LEFT */}
              <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
                <Section icon={<FiUser size={14}/>} title="Personal Information" desc="Name, gender, date of birth">
                  {/* Profile Photo */}
                  <div style={{ gridColumn:'span 2', display:'flex', alignItems:'center', gap:16, padding:'14px 16px', background:'#FAFBFC', borderRadius:10, border:'1px solid #E5E7EB' }}>
                    <div style={{ width:64, height:64, borderRadius:'50%', background:C.navyL, display:'flex', alignItems:'center', justifyContent:'center', overflow:'hidden', flexShrink:0, border:`2px solid ${C.border}` }}>
                      {profilePhotoPreview ? (
                        <img src={profilePhotoPreview} alt="Profile" style={{ width:'100%', height:'100%', objectFit:'cover' }}/>
                      ) : (
                        <FiUser size={24} style={{ color:C.navy, opacity:0.4 }}/>
                      )}
                    </div>
                    <div style={{ flex:1 }}>
                      <div style={{ fontSize:13, fontWeight:700, color:C.t1, marginBottom:4 }}>Profile Photo</div>
                      <input ref={profileRef} type="file" accept=".jpg,.jpeg,.png"
                        onChange={e => setProfilePhoto(e.target.files[0] || null)}
                        style={{ width:'100%', height:34, border:`1.5px solid ${C.border}`, borderRadius:8, padding:'0 10px', fontSize:12, color:C.t1, background:C.white, boxSizing:'border-box', cursor:'pointer', fontFamily:'inherit' }}/>
                      {profilePhoto && <span style={{ fontSize:11, color:C.green, marginTop:2 }}>✓ {profilePhoto.name}</span>}
                    </div>
                  </div>
                  <Field label="First Name" required error={errors.first_name}>
                    <input name="first_name" type="text" value={formData.first_name} onChange={handleChange} placeholder="John"
                      style={inputSt(!!errors.first_name)} onFocus={onFocus} onBlur={e => onBlur(e, !!errors.first_name)}/>
                  </Field>
                  <Field label="Last Name" required error={errors.last_name}>
                    <input name="last_name" type="text" value={formData.last_name} onChange={handleChange} placeholder="Doe"
                      style={inputSt(!!errors.last_name)} onFocus={onFocus} onBlur={e => onBlur(e, !!errors.last_name)}/>
                  </Field>
                  <Field label="Gender">
                    <div style={{ position:'relative' }}>
                      <select name="gender" value={formData.gender} onChange={handleChange}
                        style={{ ...inputSt(), cursor:'pointer', paddingRight:28, appearance:'none' }}>
                        <option value="">Select gender</option>
                        {GENDER_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                      </select>
                      <span style={{ position:'absolute', right:10, top:'50%', transform:'translateY(-50%)', pointerEvents:'none', color:C.t4, fontSize:10 }}>▾</span>
                    </div>
                  </Field>
                  <Field label="Date of Birth">
                    <DateInput value={formData.dob} onChange={e => set('dob', e.target.value)} max={new Date().toISOString().split('T')[0]}
                      style={{ ...inputSt(), paddingRight:32 }} onFocus={onFocus} onBlur={e => onBlur(e)}/>
                  </Field>
                  <Field label="Address" span2>
                    <textarea name="address" value={formData.address} onChange={handleChange} rows={3} placeholder="Enter full address"
                      style={{ ...inputSt(), height:'auto', paddingTop:10, paddingBottom:10, resize:'vertical', lineHeight:1.6 }} onFocus={onFocus} onBlur={e => onBlur(e)}/>
                  </Field>
                </Section>

                <Section icon={<FiLock size={14}/>} title={isEdit ? 'Change Password' : 'Set Password'} desc={isEdit ? 'Leave blank to keep current password' : undefined} accentColor={C.sky}>
                  {isEdit && (
                    <>
                    <div style={{ gridColumn:'span 2' }}>
                      <button type="button" onClick={toggleChangePasswordMode}
                        style={{ height:38, padding:'0 16px', borderRadius:9, border:`1.5px solid ${C.navy}`, background:C.navyL, color:C.navy, fontSize:12.5, fontWeight:700, cursor:'pointer', fontFamily:'inherit' }}>
                        {changePasswordMode ? 'Cancel Password Change' : 'Change Password'}
                      </button>
                    </div>
                    {!changePasswordMode ? (
                    <div style={{ gridColumn:'span 2', display:'flex', alignItems:'flex-start', gap:8, background:C.navyL, borderRadius:9, padding:'10px 14px', border:'1px solid #C5D9EC' }}>
                      <FiInfo size={13} style={{ color:C.navy, marginTop:1, flexShrink:0 }}/>
                      <span style={{ fontSize:12, color:C.navyM, lineHeight:1.5 }}>Password will stay unchanged unless you click Change Password.</span>
                    </div>
                    ) : null}
                    </>
                  )}
                  {changePasswordMode ? (
                  <>
                  <Field label="Password" required={!isEdit || changePasswordMode} error={errors.password}>
                    <div style={{ position:'relative' }}>
                      <input name="password" type={showPwd ? 'text' : 'password'} value={formData.password} onChange={handleChange}
                        placeholder="Min. 8 characters" autoComplete="new-password"
                        style={{ ...inputSt(!!errors.password), paddingRight:40 }} onFocus={onFocus} onBlur={e => onBlur(e, !!errors.password)}/>
                      <button type="button" onClick={() => setShowPwd(v => !v)}
                        style={{ position:'absolute', right:10, top:'50%', transform:'translateY(-50%)', background:'none', border:'none', cursor:'pointer', color:C.t4, display:'flex', padding:0 }}>
                        {showPwd ? <FiEyeOff size={15}/> : <FiEye size={15}/>}
                      </button>
                    </div>
                  </Field>
                  <Field label="Confirm Password" required={!isEdit || changePasswordMode} error={errors.confirm_password}>
                    <div style={{ position:'relative' }}>
                      <input name="confirm_password" type={showCfm ? 'text' : 'password'} value={formData.confirm_password} onChange={handleChange}
                        placeholder="Re-enter password" autoComplete="new-password"
                        style={{ ...inputSt(!!errors.confirm_password), paddingRight:40 }} onFocus={onFocus} onBlur={e => onBlur(e, !!errors.confirm_password)}/>
                      <button type="button" onClick={() => setShowCfm(v => !v)}
                        style={{ position:'absolute', right:10, top:'50%', transform:'translateY(-50%)', background:'none', border:'none', cursor:'pointer', color:C.t4, display:'flex', padding:0 }}>
                        {showCfm ? <FiEyeOff size={15}/> : <FiEye size={15}/>}
                      </button>
                    </div>
                  </Field>
                  </>
                  ) : null}
                </Section>

                {/* Documents */}
                {!isSelfProfileMode ? (
                <div style={{ background:C.white, borderRadius:12, border:`1px solid ${C.border}`, overflow:'hidden', boxShadow:'0 2px 6px rgba(13,59,94,0.06)' }}>
                  <div style={{ padding:'14px 20px', borderBottom:`1px solid ${C.borderL}`, background:'#FAFBFC', borderLeft:`3px solid ${C.sky}`, display:'flex', alignItems:'center', gap:12 }}>
                    <div style={{ width:34, height:34, borderRadius:9, background:C.skyL, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                      <FiFileText size={14} style={{ color:C.sky }}/>
                    </div>
                    <div style={{ flex:1 }}>
                      <div style={{ fontSize:14, fontWeight:800, color:C.t1, letterSpacing:'-0.02em' }}>User Documents</div>
                      <div style={{ fontSize:12, color:C.t4, marginTop:1 }}>ID proof, address proof, joining letter, agreement</div>
                    </div>
                    <span style={{ background:C.skyL, color:C.sky, padding:'2px 10px', borderRadius:20, fontSize:11, fontWeight:700, border:'1px solid #BAE6FD' }}>
                      {documents.length} doc{documents.length !== 1 ? 's' : ''}
                    </span>
                  </div>
                  <div style={{ padding:'18px 20px', display:'flex', flexDirection:'column', gap:14 }}>
                    {!isEdit && (
                      <div style={{ background:C.goldL, border:'1px solid #FDE68A', borderRadius:9, padding:'10px 14px', display:'flex', alignItems:'flex-start', gap:8 }}>
                        <FiInfo size={13} style={{ color:'#92400E', marginTop:1, flexShrink:0 }}/>
                        <span style={{ fontSize:12.5, color:'#92400E', lineHeight:1.6 }}><strong>Save the user first</strong>, then come back to edit and upload documents.</span>
                      </div>
                    )}
                    {isEdit && (
                      <div style={{ background:'#F8FAFC', border:`1px solid ${C.border}`, borderRadius:10, padding:'14px 16px' }}>
                        <div style={{ fontSize:12.5, fontWeight:700, color:C.t1, marginBottom:12 }}>Upload New Document</div>
                        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'10px 12px' }}>
                          <div style={{ display:'flex', flexDirection:'column', gap:4 }}>
                            <label style={{ fontSize:11.5, fontWeight:700, color:C.t2 }}>Document Type</label>
                            <div style={{ position:'relative' }}>
                              <select value={docType} onChange={e => setDocType(e.target.value)}
                                style={{ ...DOC_INP_ST, cursor:'pointer', paddingRight:26 }} onFocus={docFocus} onBlur={docBlur}>
                                {DOC_TYPES.map(dt => <option key={dt.value} value={dt.value}>{dt.label}</option>)}
                              </select>
                              <span style={{ position:'absolute', right:9, top:'50%', transform:'translateY(-50%)', pointerEvents:'none', color:C.t4, fontSize:10 }}>▾</span>
                            </div>
                          </div>
                          <div style={{ display:'flex', flexDirection:'column', gap:4 }}>
                            <label style={{ fontSize:11.5, fontWeight:700, color:C.t2 }}>Select File</label>
                            <input ref={fileRef} type="file" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                              onChange={e => setDocFile(e.target.files[0] || null)}
                              style={{ width:'100%', height:36, border:`1.5px solid ${C.border}`, borderRadius:8, padding:'0 10px', fontSize:12.5, color:C.t1, background:C.white, boxSizing:'border-box', cursor:'pointer', fontFamily:'inherit' }}/>
                            {docFile && <span style={{ fontSize:11, color:C.green, marginTop:2 }}>✓ {docFile.name}</span>}
                          </div>
                          <div style={{ gridColumn:'1/-1' }}>
                            <button type="button" onClick={handleDocUpload} disabled={docUploading || !docFile}
                              style={{ height:36, padding:'0 18px', background:(docUploading||!docFile)?C.border:C.sky, color:(docUploading||!docFile)?C.t4:C.white, border:'none', borderRadius:8, fontWeight:700, fontSize:13, cursor:(docUploading||!docFile)?'not-allowed':'pointer', fontFamily:'inherit', display:'flex', alignItems:'center', gap:6 }}>
                              {docUploading
                                ? <><div style={{ width:13, height:13, borderRadius:'50%', border:'2px solid rgba(255,255,255,0.4)', borderTopColor:'white', animation:'cfSpin 0.8s linear infinite' }}/> Uploading…</>
                                : <><FiUpload size={13}/> Upload Document</>}
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                    {docsLoading ? (
                      <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                        {[1,2].map(i => <div key={i} style={{ height:48, borderRadius:8, background:'linear-gradient(90deg,#F1F5F9 25%,#E2E8F0 50%,#F1F5F9 75%)', backgroundSize:'400px 100%', animation:'cfShimmer 1.4s infinite' }}/>)}
                      </div>
                    ) : documents.length === 0 ? (
                      <div style={{ textAlign:'center', padding:'24px 16px' }}>
                        <div style={{ width:42, height:42, borderRadius:'50%', background:C.skyL, display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 10px' }}>
                          <FiFileText size={18} style={{ color:C.sky, opacity:0.5 }}/>
                        </div>
                        <div style={{ fontSize:13, fontWeight:600, color:C.t2 }}>No documents yet</div>
                        <div style={{ fontSize:12, color:C.t4, marginTop:3 }}>{isEdit ? 'Upload using the form above.' : 'Save the user first, then upload.'}</div>
                      </div>
                    ) : (
                      <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                        <div style={{ fontSize:12, fontWeight:700, color:C.t2, marginBottom:2 }}>Uploaded Documents ({documents.length})</div>
                        {documents.map(doc => {
                          const isDel = docDeleting === doc.id;
                          const docName = fileName(doc.file || doc.document);
                          const uploadDate = doc.created_at ? fmtDate(doc.created_at) : '';
                          return (
                            <div key={doc.id} style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 12px', background:'#FAFBFC', border:`1px solid ${C.border}`, borderRadius:9, opacity:isDel?0.5:1, transition:'all 0.12s' }}>
                              <div style={{ width:32, height:32, borderRadius:8, background:C.skyL, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                                <FiFile size={14} style={{ color:C.sky }}/>
                              </div>
                              <div style={{ flex:1, minWidth:0 }}>
                                <div style={{ fontSize:12.5, fontWeight:600, color:C.t1, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{docName}</div>
                                <div style={{ fontSize:11, color:C.t4, marginTop:2 }}>{docTypeLabel(doc.document_type)}{uploadDate && ` · ${uploadDate}`}</div>
                              </div>
                              {(doc.file || doc.document) && (
                                <a href={doc.file||doc.document} target="_blank" rel="noopener noreferrer"
                                  style={{ display:'flex', alignItems:'center', gap:4, fontSize:12, color:C.sky, fontWeight:600, padding:'4px 10px', background:C.skyL, borderRadius:7, textDecoration:'none', border:'1px solid #BAE6FD', flexShrink:0 }}>
                                  <FiDownload size={11}/> View
                                </a>
                              )}
                              <button type="button" onClick={() => handleDocDelete(doc.id)} disabled={isDel}
                                style={{ width:30, height:30, borderRadius:7, border:`1px solid ${C.redB}`, background:C.white, color:C.red, cursor:isDel?'not-allowed':'pointer', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0, opacity:isDel?0.5:1 }}
                                onMouseEnter={e => { if(!isDel) e.currentTarget.style.background=C.redL; }}
                                onMouseLeave={e => { e.currentTarget.style.background=C.white; }}>
                                {isDel
                                  ? <div style={{ width:11, height:11, borderRadius:'50%', border:`2px solid ${C.red}`, borderTopColor:'transparent', animation:'cfSpin 0.8s linear infinite' }}/>
                                  : <FiTrash2 size={12}/>}
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
                ) : null}
              </div>

              {/* RIGHT */}
              <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
                <Section icon={<FiMail size={14}/>} title="Contact Information" desc="Email and phone number" accentColor={C.green}>
                  <Field label="Email Address" required error={errors.email} span2>
                    <input name="email" type="email" value={formData.email} onChange={handleChange} placeholder="john@example.com" autoComplete="email"
                      disabled={isSelfProfileMode}
                      style={{ ...inputSt(!!errors.email), ...(isSelfProfileMode ? { background:'#F3F4F6', color:C.t4 } : {}) }} onFocus={onFocus} onBlur={e => onBlur(e, !!errors.email)}/>
                  </Field>
                  <Field label="Mobile Number" required error={errors.mobile_number} span2>
                    <div style={{ position:'relative' }}>
                      <FiPhone size={13} style={{ position:'absolute', left:11, top:'50%', transform:'translateY(-50%)', color:C.t4, pointerEvents:'none' }}/>
                      <input name="mobile_number" type="tel" value={formData.mobile_number} onChange={e => set('mobile_number', e.target.value.replace(/[^0-9]/g, ''))} placeholder="10-digit number" maxLength={10}
                        disabled={isSelfProfileMode}
                        style={{ ...inputSt(!!errors.mobile_number), paddingLeft:34, ...(isSelfProfileMode ? { background:'#F3F4F6', color:C.t4 } : {}) }} onFocus={onFocus} onBlur={e => onBlur(e, !!errors.mobile_number)}/>
                    </div>
                  </Field>
                </Section>

                {!isSelfProfileMode ? (
                <Section icon={<FiShield size={14}/>} title="Role & Organisation" desc="Role, designation, reporting manager" accentColor={C.purple}>
                  <Field label="Role" required error={errors.role} span2
                    hint={roles.length === 0 ? 'No roles found — create roles in Roles & Permissions first' : undefined}>
                    <div style={{ position:'relative' }}>
                      <select name="role" value={formData.role} onChange={handleChange}
                        style={{ ...inputSt(!!errors.role), cursor:'pointer', paddingRight:28, appearance:'none' }}>
                        <option value="">— Select Role —</option>
                        {roles.map(r => <option key={r.id} value={String(r.id)}>{r.name}</option>)}
                      </select>
                      <span style={{ position:'absolute', right:10, top:'50%', transform:'translateY(-50%)', pointerEvents:'none', color:C.t4, fontSize:10 }}>▾</span>
                    </div>
                  </Field>
                  <Field label="Designation">
                    <div style={{ position:'relative' }}>
                      <select name="designation" value={formData.designation} onChange={handleChange}
                        style={{ ...inputSt(), cursor:'pointer', paddingRight:28, appearance:'none' }}>
                        <option value="">— Select —</option>
                        {DESIGNATION_OPTIONS.map(d => <option key={d} value={d}>{d}</option>)}
                      </select>
                      <span style={{ position:'absolute', right:10, top:'50%', transform:'translateY(-50%)', pointerEvents:'none', color:C.t4, fontSize:10 }}>▾</span>
                    </div>
                  </Field>
                  <Field label="Reporting Manager">
                    <div style={{ position:'relative' }}>
                      <select name="reporting_manager" value={formData.reporting_manager} onChange={handleChange}
                        style={{ ...inputSt(), cursor:'pointer', paddingRight:28, appearance:'none' }}>
                        <option value="">None (Top-level)</option>
                        {managers.filter(u => !isEdit || u.id !== parseInt(id)).map(u => {
                          const nm = `${u.first_name||''} ${u.last_name||''}`.trim() || u.email;
                          return <option key={u.id} value={String(u.id)}>{nm}{u.designation ? ` · ${u.designation}` : ''}</option>;
                        })}
                      </select>
                      <span style={{ position:'absolute', right:10, top:'50%', transform:'translateY(-50%)', pointerEvents:'none', color:C.t4, fontSize:10 }}>▾</span>
                    </div>
                  </Field>
                </Section>
                ) : null}

                {/* Payroll & Incentive */}
                {!isSelfProfileMode ? (
                <Section icon={<FiShield size={14}/>} title="Payroll & Incentive" desc="Salary and incentive parameters" accentColor={C.gold}>
                  <Field label="Monthly Salary (₹)" error={errors.salary} hint="Gross monthly CTC.">
                    <input name="salary" type="number" min="0" step="1" value={formData.salary} onChange={handleChange}
                      placeholder="e.g. 25000"
                      style={inputSt(!!errors.salary)} onFocus={onFocus} onBlur={e => onBlur(e, !!errors.salary)}/>
                  </Field>
                  <Field label="Cut-off Amount (₹)" required error={errors.cutoff_amount} hint="Minimum disbursed amount for incentive.">
                    <input name="cutoff_amount" type="number" min="0" step="0.01" value={formData.cutoff_amount} onChange={handleChange}
                      placeholder="e.g. 100000"
                      style={inputSt(!!errors.cutoff_amount)} onFocus={onFocus} onBlur={e => onBlur(e, !!errors.cutoff_amount)}/>
                  </Field>
                  <Field label="Incentive %" required error={errors.incentive_percentage} hint="Percentage of disbursed amount." span2>
                    <input name="incentive_percentage" type="number" min="0" max="100" step="0.01" value={formData.incentive_percentage} onChange={handleChange}
                      placeholder="e.g. 2.00"
                      style={{ ...inputSt(!!errors.incentive_percentage), maxWidth:220 }} onFocus={onFocus} onBlur={e => onBlur(e, !!errors.incentive_percentage)}/>
                  </Field>
                </Section>
                ) : null}

                {/* Action bar */}
                <div className="user-action-bar" style={{ background:C.white, borderRadius:12, border:`1px solid ${C.border}`, padding:'16px 20px', boxShadow:'0 2px 6px rgba(13,59,94,0.06)', display:'flex', alignItems:'center', justifyContent:'space-between', gap:12, flexWrap:'wrap' }}>
                  <div style={{ fontSize:12.5 }}>
                    {errCount > 0
                      ? <span style={{ display:'flex', alignItems:'center', gap:5, color:C.red, fontWeight:600 }}><FiAlertCircle size={13}/> {errCount} error{errCount !== 1 ? 's' : ''} to fix</span>
                      : <span style={{ display:'flex', alignItems:'center', gap:5, color:C.green, fontWeight:600 }}><FiCheckCircle size={13}/> {isEdit ? 'Ready to update' : 'Ready to create'}</span>}
                  </div>
                  <div style={{ display:'flex', gap:10, flexWrap:'wrap' }}>
                    <button type="button" onClick={() => navigate('/users')} disabled={saving}
                      style={{ height:38, padding:'0 18px', border:`1.5px solid ${C.border}`, borderRadius:9, background:C.white, color:C.t3, fontSize:13, fontWeight:600, cursor:'pointer', fontFamily:'inherit', transition:'all 0.12s' }}
                      onMouseEnter={e => { e.currentTarget.style.borderColor=C.t3; e.currentTarget.style.color=C.t1; }}
                      onMouseLeave={e => { e.currentTarget.style.borderColor=C.border; e.currentTarget.style.color=C.t3; }}>
                      Cancel
                    </button>
                    <button type="submit" disabled={saving}
                      style={{ height:38, padding:'0 24px', minWidth:160, background:saving?C.navyM:C.gold, color:saving?'#fff':'#0D1B2A', border:'none', borderRadius:9, fontSize:13, fontWeight:800, cursor:saving?'not-allowed':'pointer', fontFamily:'inherit', display:'flex', alignItems:'center', justifyContent:'center', gap:7, boxShadow:'0 2px 10px rgba(245,166,35,0.40)', transition:'all 0.12s' }}
                      onMouseEnter={e => { if(!saving){ e.currentTarget.style.background='#FBBF4A'; e.currentTarget.style.transform='translateY(-1px)'; } }}
                      onMouseLeave={e => { if(!saving){ e.currentTarget.style.background=C.gold; e.currentTarget.style.transform=''; } }}>
                      {saving
                        ? <><div style={{ width:15, height:15, borderRadius:'50%', border:'2px solid rgba(255,255,255,0.35)', borderTopColor:'#fff', animation:'cfSpin 0.7s linear infinite' }}/> Saving…</>
                        : <><FiSave size={14}/> {isEdit ? 'Update User' : 'Create User'}</>}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </form>
        </div>
      </Layout>
    </>
  );
}
